import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';

/**
 * Terms of Service. Plain-English copy aimed at a hobby-project audience.
 *
 * NOT LEGAL ADVICE. This is a good-faith draft covering the standard points
 * a small practice site should address. If FlareCode ever graduates to a
 * real commercial product, have a lawyer review.
 *
 * Update `LAST_UPDATED` when the text materially changes.
 */

const LAST_UPDATED = '2026-04-19';

const TERMS_MD = `
# Terms of Service

**Last updated:** ${LAST_UPDATED}

Welcome to FlareCode. These terms describe how you can use the site and what
to expect from us. By creating an account or submitting code, you agree to
these terms.

## 1. What FlareCode is

FlareCode is a personal-project coding-practice platform. You write solutions
to algorithm problems in your browser and run them against test cases on our
infrastructure. It's a small hobby site, not a commercial service.

## 2. Who can use it

You can use FlareCode if you are at least 13 years old. You agree to use the
site in good faith: no impersonation, no abuse of other users, no attempts to
circumvent the judge or rate limits.

Only create one account per person. Don't share accounts. Choose a username
that isn't someone else's real name or trademark unless it's yours.

## 3. Your code

You own the code you submit. By submitting code you give us a limited license
to store it, run it on our judge, and display it back to you. We don't claim
ownership, resell your code, or share it outside the service.

Other users can see that you made a submission (problem, language, verdict,
timestamp) on your public profile, but they cannot see your source code. Only
you can see your own submission source.

## 4. Acceptable use

You agree not to:

- Submit code designed to abuse the judge (fork bombs, resource exhaustion,
  attempts to escape the sandbox, network exfiltration attempts, etc.)
- Submit code that infringes someone else's intellectual property
- Scrape problems or test cases for republication elsewhere
- Use FlareCode in any way that violates applicable laws

We reserve the right to remove content and terminate accounts that violate
these rules. For a hobby site like this, "terminate" means we'll email you
and disable the account; we're not trying to be ominous about it.

## 5. Availability and changes

FlareCode is provided as-is. We don't guarantee uptime, performance, or
that the site will continue to exist. Problems may be added or removed.
Your submissions may be deleted if we need to retire the service or reset
data. We'll try to give notice if that ever happens.

We may change these terms. When we do, the \`Last updated\` date above
changes. Continued use after changes means you accept the new terms.

## 6. Third-party services

FlareCode runs on Cloudflare infrastructure (Workers, D1, R2, Durable
Objects, Containers). Your data lives on their systems. Their privacy and
security terms apply to that underlying storage.

## 7. No warranty, limited liability

FlareCode is provided "as is" with no warranties of any kind, express or
implied. We are not liable for any damages arising from your use of the site,
to the maximum extent permitted by law.

## 8. Contact

Questions about these terms? Open an issue on the project repository or
email the address shown on our GitHub profile.
`;

export function TermsPage() {
  return (
    <section className="legal-page">
      <div className="markdown-body">
        <ReactMarkdown remarkPlugins={[remarkGfm]}>{TERMS_MD}</ReactMarkdown>
      </div>
    </section>
  );
}
